import * as React from 'react'
import { CaretDown, Check } from '@phosphor-icons/react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../dropdown-menu'
import { cn } from '../../../lib/utils'
import {
  formFieldBaseClass,
  formFieldFocusClass,
  formFieldOpenClass,
} from './formFieldStyles'

type FormSelectOption = {
  label: string
  value: string
}

interface FormSelectProps {
  id?: string
  value: string
  onValueChange: (value: string) => void
  options: FormSelectOption[]
  placeholder: string
  disabled?: boolean
  triggerClassName?: string
  contentClassName?: string
}

export function FormSelect({
  id,
  value,
  onValueChange,
  options,
  placeholder,
  disabled = false,
  triggerClassName,
  contentClassName,
}: FormSelectProps) {
  const triggerRef = React.useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = React.useState(false)
  const [menuWidth, setMenuWidth] = React.useState<number>()

  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    setMenuWidth(triggerRef.current.getBoundingClientRect().width)
  }, [open])

  const selectedOption = options.find((option) => option.value === value)

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          id={id}
          ref={triggerRef}
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-11 w-full items-center justify-between px-4 text-left font-sans focus:outline-none disabled:pointer-events-none disabled:opacity-50',
            formFieldBaseClass,
            formFieldFocusClass,
            formFieldOpenClass,
            !selectedOption && 'text-ink-400',
            triggerClassName
          )}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <span className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <CaretDown size={16} className="shrink-0 text-primary-300" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className={cn('z-dropdown p-1.5', contentClassName)}
        style={menuWidth ? { width: menuWidth } : undefined}
      >
        {options.map((option) => {
          const isSelected = option.value === value

          return (
            <DropdownMenuItem
              key={option.value}
              onSelect={() => onValueChange(option.value)}
              className={cn(
                'justify-between gap-3 rounded-[10px] px-3 py-2 text-fs-400 text-ink-700',
                isSelected && 'bg-[rgba(36,34,30,0.04)] font-medium'
              )}
            >
              <span>{option.label}</span>
              {isSelected ? (
                <Check size={16} className="shrink-0 text-primary-400" />
              ) : null}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
